import type React from 'react';
import { Badge } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { FeatureImpactComboRow, FeatureImpactSimpleRow } from './types';
import { formatPct01, formatPct01Number, formatSignedPct, toNumber } from './helpers';

const variantForDirection = (direction: FeatureImpactSimpleRow['direction']): React.ComponentProps<typeof Badge>['variant'] => {
    switch (direction) {
        case 'positive':
            return 'success';
        case 'negative':
            return 'danger';
        case 'mixed':
            return 'warning';
        case 'neutral':
        default:
            return 'secondary';
    }
};

const labelForDirection = (direction: FeatureImpactSimpleRow['direction']): string => {
    switch (direction) {
        case 'positive':
            return 'Positive';
        case 'negative':
            return 'Negative';
        case 'mixed':
            return 'Mixed';
        case 'neutral':
        default:
            return 'Neutral';
    }
};

export const needleMoverColumns: ColumnDef<FeatureImpactSimpleRow>[] = [
    {
        header: 'Rank',
        accessorKey: 'rank',
        size: 70
    },
    {
        header: 'Feature',
        accessorKey: 'featureKey',
        size: 300,
        cell: ({ row }) => <span className="text-white text-xs">{row.original.featureKey}</span>
    },
    {
        header: 'Impact',
        id: 'impact',
        size: 90,
        cell: ({ row }) => <span className="text-white">{formatPct01Number(toNumber(row.original.impactScore), 1)}</span>
    },
    {
        header: 'Dir',
        accessorKey: 'direction',
        size: 100,
        cell: ({ row }) => <Badge variant={variantForDirection(row.original.direction)}>{labelForDirection(row.original.direction)}</Badge>
    },
    {
        header: 'Δ',
        id: 'delta',
        size: 100,
        cell: ({ row }) => {
            const v = toNumber(row.original.delta);
            if (!Number.isFinite(v) || v === 0) return <span className="text-zinc-500">-</span>;
            return <span className={v >= 0 ? 'text-green-400' : 'text-red-400'}>{formatSignedPct(v, 2)}</span>;
        }
    },
    {
        header: 'HO N',
        accessorKey: 'holdoutSampleCount',
        size: 90,
        cell: ({ row }) => <span className="text-zinc-200">{row.original.holdoutSampleCount.toLocaleString()}</span>
    },
    {
        header: 'Missing',
        accessorKey: 'missingRate',
        size: 90,
        cell: ({ row }) => <span className="text-zinc-300">{formatPct01(row.original.missingRate, 1)}</span>
    },
    {
        header: 'Bins',
        accessorKey: 'binCountUsed',
        size: 70,
        cell: ({ row }) => <span className="text-zinc-200">{row.original.binCountUsed}</span>
    }
];

export const comboFeatureColumns: ColumnDef<FeatureImpactComboRow>[] = [
    {
        header: 'Feature',
        accessorKey: 'featureKey',
        size: 300,
        cell: ({ row }) => <span className="text-white text-xs">{row.original.featureKey}</span>
    },
    {
        header: 'In Top',
        id: 'inTop',
        size: 100,
        cell: ({ row }) => (
            <span className="text-white">
                {row.original.inTopResultsCount}/{row.original.topResultsN}
            </span>
        )
    },
    {
        header: 'Occur',
        accessorKey: 'conditionOccurrences',
        size: 90,
        cell: ({ row }) => <span className="text-zinc-200">{row.original.conditionOccurrences.toLocaleString()}</span>
    },
    {
        header: 'Op',
        accessorKey: 'topOperator',
        size: 70,
        cell: ({ row }) => (row.original.topOperator ? <Badge variant="secondary">{row.original.topOperator}</Badge> : '-')
    },
    {
        header: 'Avg Lift',
        accessorKey: 'avgLift',
        size: 100,
        cell: ({ row }) => {
            const v = row.original.avgLift;
            if (v === null) return <span className="text-zinc-500">-</span>;
            return <span className={v >= 0 ? 'text-green-400' : 'text-red-400'}>{formatSignedPct(v, 2)}</span>;
        }
    },
    {
        header: 'Avg HO',
        accessorKey: 'avgHoldoutLift',
        size: 100,
        cell: ({ row }) => {
            const v = row.original.avgHoldoutLift;
            if (v === null) return <span className="text-zinc-500">-</span>;
            return <span className={v >= 0 ? 'text-green-400' : 'text-red-400'}>{formatSignedPct(v, 2)}</span>;
        }
    },
    {
        header: 'Avg Sweet',
        accessorKey: 'avgSweetScore',
        size: 110,
        cell: ({ row }) => {
            const v = row.original.avgSweetScore;
            if (v === null) return <span className="text-zinc-500">-</span>;
            return <span className="text-white">{v.toFixed(4)}</span>;
        }
    }
];
